// REACT
import { useEffect, useState } from 'react';

// YARN
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

// SERVICES
import CultureGroupCard from './CultureGroupCard';
import AddEditCultureGroupModal from './AddEditCultureGroup';
import useCultureGroupAction from 'utils/hooks/useCultureGroupAction';
import useAutoComplete from 'utils/hooks/useAutoComplete';

// THEMES
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';

const MixedFarmingCultureGroup = ({
  ownerId,
  cultures,
  mixedFarmingCultureGroup,
  loadMixedFarmingGroupData,
}) => {
  const [cultureGroupFiltered, setCultureGroupFiltered] = useState(mixedFarmingCultureGroup);
  const [modal, setModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const { t } = useTranslation();

  const formSuccess = async () => {
    setModal(false);
    setEditData(null);
    loadMixedFarmingGroupData();
  };

  const { renderDeleteDialog, handleDeleteDialogOpen, handleEditDialogOpen } =
    useCultureGroupAction(
      ownerId,
      mixedFarmingCultureGroup,
      loadMixedFarmingGroupData,
      setModal,
      setEditData,
      'mixedFarmingCultureGroup'
    );

  const resetCultureGroupValue = () => {
    setInputValue('');
    setOptions(filterData('', mixedFarmingCultureGroup));
  };

  const { renderAutoComplete, inputValue, setInputValue, filterData, setOptions } = useAutoComplete(
    {
      data: mixedFarmingCultureGroup,
      getOptionLabel: (option) => option.name,
      resetValue: resetCultureGroupValue,
      isMetaculture: false,
    }
  );

  useEffect(() => {
    if (inputValue) {
      const filteredGroups = mixedFarmingCultureGroup?.filter((group) =>
        group.name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setCultureGroupFiltered(filteredGroups);
    } else {
      setCultureGroupFiltered(mixedFarmingCultureGroup);
    }
  }, [inputValue, mixedFarmingCultureGroup]);

  const filterCulturesExistingInGroups = (cultures, cultureGroups, editData) => {
    const cultureIdsInGroups = cultureGroups
      ?.filter((group) => !editData || group.id !== editData.id)
      ?.flatMap((group) => group?.cultures?.map((culture) => culture.id));

    return cultures?.filter((culture) => !cultureIdsInGroups?.includes(culture.id));
  };

  const filteredCultures = filterCulturesExistingInGroups(
    cultures,
    mixedFarmingCultureGroup,
    editData
  );

  return (
    <div className="section">
      <div>
        <div className="override">
          <div className="contain_header_icon search-actions-wrapper">
            {renderAutoComplete()}
            <ThemeProvider theme={customButtonTheme}>
              <Button
                onClick={() => {
                  setModal(!modal), setEditData(null);
                }}
              >
                {t('common.add')} <AddIcon />
              </Button>
            </ThemeProvider>
          </div>
        </div>
        <div className="card-container-cultureGroup">
          <CultureGroupCard
            cultureGroup={cultureGroupFiltered}
            onDeleteClick={handleDeleteDialogOpen}
            onEditClick={handleEditDialogOpen}
          />
        </div>
      </div>
      {modal && (
        <AddEditCultureGroupModal
          modal={modal}
          setModal={setModal}
          onSubmitSuccess={formSuccess}
          edit={editData}
          cooperativeId={ownerId}
          cultures={filteredCultures}
          cultureGroup={mixedFarmingCultureGroup}
        />
      )}
      {renderDeleteDialog()}
    </div>
  );
};

export default MixedFarmingCultureGroup;
